<script setup lang="ts">
/**
 * Taste rows — the `Taste` frame of Person card (3138:95814) and the 07b Taste card of
 * the Gift quiz (3222:17260): "Styles / Colors / Brands" label (88 wide) + Taste preview.
 * A row with nothing picked is left out; `loading` keeps the height with skeletons.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { FairSkeleton } from '@/ui/primitives/FairSkeleton'
import type { FairTastePreviewItem, FairTastePreviewKind } from '@/ui/tokens/fair-taste-preview.data'
import FairTastePreview from './FairTastePreview.vue'

const props = withDefaults(
  defineProps<{
    styles?: FairTastePreviewItem[]
    colors?: FairTastePreviewItem[]
    brands?: FairTastePreviewItem[]
    loading?: boolean
  }>(),
  { styles: () => [], colors: () => [], brands: () => [], loading: false },
)

const { t } = useI18n()

const rows = computed(() =>
  (
    [
      { kind: 'style', key: 'styles', items: props.styles },
      { kind: 'color', key: 'colors', items: props.colors },
      { kind: 'brand', key: 'brands', items: props.brands },
    ] as { kind: FairTastePreviewKind; key: string; items: FairTastePreviewItem[] }[]
  ).filter((row) => row.items.length),
)
</script>

<template>
  <div
    class="fair-taste-details"
    data-figma="Taste"
  >
    <template v-if="loading && !rows.length">
      <div
        v-for="row in 3"
        :key="row"
        class="fair-taste-details__row"
      >
        <FairSkeleton
          :width="48"
          :height="10"
        />
        <FairSkeleton
          :width="108"
          :height="28"
        />
      </div>
    </template>
    <template v-else>
      <div
        v-for="row in rows"
        :key="row.kind"
        class="fair-taste-details__row"
        :data-figma="`Detail / ${row.key}`"
      >
        <span class="fair-taste-details__label">{{ t(`people.taste.${row.key}`) }}</span>
        <FairTastePreview
          :kind="row.kind"
          :items="row.items"
          :label="t(`people.taste.${row.key}`)"
        />
      </div>
    </template>
  </div>
</template>
